/* ---------------------------------------------------------------------------
   Live microphone level, for the input meter the UI shows while recording.

   Reads the mic through an AnalyserNode on the shared AudioContext. The node is
   never connected to the destination — routing a microphone to the speakers in
   a party game is a feedback howl waiting to happen.
   --------------------------------------------------------------------------- */

import { getAudioContext, isAudioSupported } from "./audio";

/** How fast the meter rises and falls. Fast attack, slow decay reads best. */
const LEVEL_ATTACK = 0.5;
const LEVEL_DECAY = 0.12;
/** RMS of ordinary speech is well under 1; scale it into a usable meter range. */
const LEVEL_GAIN = 3.5;

export interface MicLevel {
  /** Smoothed 0..1 value. Drive the meter bar with this. */
  level: number;
  /** Instantaneous peak amplitude, 0..1. Light a clip indicator above ~0.98. */
  peak: number;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

export class MicMeter {
  private readonly analyser: AnalyserNode;
  private readonly source: MediaStreamAudioSourceNode;
  private floats = new Float32Array(0);
  private bytes = new Uint8Array(0);
  private smoothed = 0;
  private lastPeak = 0;
  private disposed = false;

  constructor(analyser: AnalyserNode, source: MediaStreamAudioSourceNode) {
    this.analyser = analyser;
    this.source = source;
    this.floats = new Float32Array(analyser.fftSize);
    this.bytes = new Uint8Array(analyser.fftSize);
  }

  /** Sample the mic. Call once per animation frame. */
  sample(): MicLevel {
    if (this.disposed) return { level: 0, peak: 0 };

    let sumSquares = 0;
    let peak = 0;

    if (typeof this.analyser.getFloatTimeDomainData === "function") {
      const samples = this.floats;
      this.analyser.getFloatTimeDomainData(samples);
      for (let i = 0; i < samples.length; i++) {
        const value = samples[i];
        sumSquares += value * value;
        const magnitude = Math.abs(value);
        if (magnitude > peak) peak = magnitude;
      }
      sumSquares /= samples.length;
    } else {
      // Older Safari only exposes the byte-domain view.
      const samples = this.bytes;
      this.analyser.getByteTimeDomainData(samples);
      for (let i = 0; i < samples.length; i++) {
        const value = (samples[i] - 128) / 128;
        sumSquares += value * value;
        const magnitude = Math.abs(value);
        if (magnitude > peak) peak = magnitude;
      }
      sumSquares /= samples.length;
    }

    const target = clamp01(Math.sqrt(sumSquares) * LEVEL_GAIN);
    const smoothing = target > this.smoothed ? LEVEL_ATTACK : LEVEL_DECAY;
    this.smoothed += (target - this.smoothed) * smoothing;
    this.lastPeak = clamp01(peak);

    return { level: this.smoothed, peak: this.lastPeak };
  }

  /** Last sampled values, without re-reading the analyser. */
  get current(): MicLevel {
    return { level: this.smoothed, peak: this.lastPeak };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.source.disconnect();
      this.analyser.disconnect();
    } catch {
      /* Nodes already detached. */
    }
  }
}

/**
 * Build a meter for a microphone stream.
 * Returns null when Web Audio is unavailable — the meter is a nicety, not a
 * reason to block a player from recording.
 */
export function createMicMeter(stream: MediaStream): MicMeter | null {
  if (!isAudioSupported()) return null;
  try {
    const ctx = getAudioContext();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.4;
    source.connect(analyser);
    return new MicMeter(analyser, source);
  } catch {
    return null;
  }
}
